import i18next from 'i18next';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';

import { Dropdown, ListView, SearchBar } from '@commons/utils/styledLibraryComponents';
import { Tags } from '@commons/Tags';
import EmptyState from '@commons/EmptyState';
import Text, { ENUM_COLORS, ENUM_FONTS } from '@commons/Text';

import { externalClient as externalClientService } from '@services/externalClient';

import { Container, HeaderContainer } from './styledComponents';

const DEFAULT_ORDER_BY = 'name';
const DEFAULT_ORDER_TYPE = 'asc';
const DEFAULT_MAX_PER_PAGE = 100;
const FIRST_PAGE = 1;

// Platforms can have dozens of stores: cap the panel height so it does not overflow the modal
const STORE_DROPDOWN_LIST_STYLE = { maxHeight: '200px' };

const getColumns = () => [
  {
    id: 'name',
    propertyKey: 'name',
    name: i18next.t('EXTERNAL_CLIENTS.CREATION.CLONE_COLUMN_CLIENT'),
    render: (name) => (
      <Text color={ENUM_COLORS.DARKEST} font={ENUM_FONTS.TEXT_SMALL_HEIGHT_16}>
        {name}
      </Text>
    ),
  },
  {
    id: 'externalReference',
    propertyKey: 'externalReference',
    name: i18next.t('EXTERNAL_CLIENTS.CREATION.CLONE_COLUMN_EXTERNAL_REFERENCE'),
    render: (externalReference) => (
      <Text color={ENUM_COLORS.DARKEST} font={ENUM_FONTS.TEXT_SMALL_HEIGHT_16}>
        {externalReference || '-'}
      </Text>
    ),
  },
  {
    id: 'stores',
    propertyKey: 'stores',
    name: i18next.t('EXTERNAL_CLIENTS.CREATION.CLONE_COLUMN_STORES'),
    disableSort: true,
    render: (stores) => <Tags items={(stores || []).map(({ name }) => name)} />,
  },
];

/**
 * Multi-select external client picker used to add a product to catalogs: same interface as the
 * catalog clone picker, without the product count column. The list only shows the clients not yet
 * associated to the product (server-side `excludedProductId` filter) within the given stores.
 * Selection is owned by the parent so it survives modal refreshes.
 */
const ExternalClientSelectionList = (props) => {
  const { stores, excludedProductId, selectedClientIds, onSelectionChange, onFetchError } = props;

  const [columns] = useState(getColumns());

  const [externalClients, setExternalClients] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const [search, setSearch] = useState('');
  const [orderBy, setOrderBy] = useState(DEFAULT_ORDER_BY);
  const [orderType, setOrderType] = useState(DEFAULT_ORDER_TYPE);
  const [currentPage, setCurrentPage] = useState(FIRST_PAGE);
  const [maxPerPage, setMaxPerPage] = useState(DEFAULT_MAX_PER_PAGE);

  const [selectedStores, setSelectedStores] = useState(stores);

  const selectedStoreIds = selectedStores.map(({ id }) => id);

  // Memoized: ListView re-derives and re-reports its selection on every `data` identity change.
  const listData = useMemo(
    () =>
      externalClients.map((externalClientRow) => ({
        ...externalClientRow,
        isRowSelected: selectedClientIds.includes(externalClientRow.id),
      })),
    [externalClients, selectedClientIds],
  );

  const fetchExternalClients = async () => {
    if (!selectedStoreIds.length) {
      setExternalClients([]);
      setTotalCount(0);

      return;
    }

    setIsLoading(true);

    try {
      const { externalClients: data, totalCount: count } =
        await externalClientService.getPaginatedExternalClients(
          { page: currentPage, limit: maxPerPage, search: search || '', orderBy, orderType },
          { storeIds: selectedStoreIds, excludedProductId },
        );

      setExternalClients(data || []);
      setTotalCount(count || 0);
    } catch {
      setExternalClients([]);
      setTotalCount(0);
      onFetchError();
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchExternalClients();
  }, [search, orderBy, orderType, currentPage, maxPerPage, selectedStores]);

  const handleSelection = (selectedItems) => {
    const visibleIds = externalClients.map(({ id }) => id);
    const selectedVisibleIds = selectedItems.map(({ id }) => id);

    // Server-side paging: ListView only reports the visible rows, so ids selected on other pages
    // are preserved and merged with the current page's selection.
    const preservedIds = selectedClientIds.filter((id) => !visibleIds.includes(id));

    const updatedSelection = [...preservedIds, ...selectedVisibleIds];

    const isSelectionUnchanged =
      updatedSelection.length === selectedClientIds.length &&
      updatedSelection.every((id) => selectedClientIds.includes(id));

    if (isSelectionUnchanged) {
      return;
    }

    onSelectionChange(updatedSelection);
  };

  const handleSearchInputChange = (value) => {
    setSearch(value);
    setCurrentPage(FIRST_PAGE);
  };

  const handleMaxPerPageChange = (value) => {
    setMaxPerPage(value);
    setCurrentPage(FIRST_PAGE);
  };

  const handleStoreFilterChange = (items) => {
    setSelectedStores(items);
    setCurrentPage(FIRST_PAGE);
  };

  return (
    <Container>
      <ListView
        columns={columns}
        countElements={totalCount}
        data={listData}
        defaultCurrentPage={currentPage}
        defaultMaxPerPage={maxPerPage}
        defaultOrderBy={orderBy}
        defaultOrderType={orderType}
        defaultSearchInput={search}
        handleCurrentPageChange={setCurrentPage}
        handleMaxPerPageChange={handleMaxPerPageChange}
        handleOrderByChange={setOrderBy}
        handleOrderTypeChange={setOrderType}
        isLoading={isLoading}
        padding={'6px 24px 0px 24px'}
        renderEmptyState={() => <EmptyState label={i18next.t('GENERAL.NO_RESULT')} />}
        renderFilterButton={() => (
          <HeaderContainer>
            <SearchBar
              placeholder={i18next.t('GENERAL.SEARCH')}
              setValue={handleSearchInputChange}
              value={search}
            />
            <Dropdown
              customDropdownListStyle={STORE_DROPDOWN_LIST_STYLE}
              iconSrc={'/images/inpulse/store-black-small.svg'}
              isUniqueSelection={false}
              items={stores}
              placeholder={i18next.t('EXTERNAL_CLIENTS.CREATION.CLONE_COLUMN_STORES')}
              selectedItems={selectedStores}
              isRequired
              onSelectionChange={handleStoreFilterChange}
            />
          </HeaderContainer>
        )}
        setSelectedItems={handleSelection}
        forceEnableSelection
        hideAllPerPageOption
        hideSearchbar
        onQueryParamsChange={() => true}
      />
    </Container>
  );
};

ExternalClientSelectionList.propTypes = {
  stores: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
    }),
  ).isRequired,
  excludedProductId: PropTypes.string.isRequired,
  selectedClientIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  onSelectionChange: PropTypes.func.isRequired,
  onFetchError: PropTypes.func.isRequired,
};

export default ExternalClientSelectionList;
